import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ROUTES } from "../routes";
import axiosInstance from "../features/axiosInstance/axiosInstance";
import { useTranslation } from 'react-i18next';
import PopupContainer from "../components/popup/PopupContainer";

const EmailConfirmationPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [isResending, setIsResending] = useState(false);
  const [timer, setTimer] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (location.state?.email) {
      localStorage.setItem("pendingConfirmationEmail", location.state.email);
    } else {
      const storedEmail = localStorage.getItem("pendingConfirmationEmail");
      if (!storedEmail) {
        navigate(ROUTES.LOGIN);
      }
    }
  }, [location.state, navigate]);



  const userEmail =
    location.state?.email ||
    localStorage.getItem("pendingConfirmationEmail") ||
    "your@email.com";

  const handleOpenGmail = () => {
    window.open("https://mail.google.com", "_blank");
  };

  const handleOpenOutlook = () => {
    window.open("https://outlook.office.com/mail/", "_blank");
  };

  const handleResendEmail = async () => {
    const email = userEmail;
    setIsResending(true);
    setErrorMessage("");
    try {
      await axiosInstance.post("/account/resend-confirmation-email", {
        email: email,
      });
      setTimer(30);
      const countdown = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(countdown);
            setIsResending(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      setErrorMessage(t('emailConfirmation.errors.resendFailed'));
      setIsResending(false);
    }
  };

  return (
    <PopupContainer>
          <div className="flex justify-center mb-6">
            <svg className="w-16 h-16 text-grey-50 dark:text-grey-70" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0-8.953 5.468a1.5 1.5 0 0 1-1.594 0L2.25 6.75" />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-center mb-4 text-grey-20 dark:text-white">
            {t('emailConfirmation.title')}
          </h1>

          <p className="text-center mb-2 text-grey-30 dark:text-grey-80">
            {t('emailConfirmation.message')}
          </p>
          <div className="text-center mb-4">
            <span className="bg-primary-95 dark:bg-primary-20 text-primary-30 dark:text-primary-90 px-3 py-1.5 rounded-full inline-block text-center font-medium text-sm">
              {userEmail}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-6">
            <button
              onClick={handleOpenGmail}
              className="btn btn-secondary flex items-center justify-center gap-2 h-[42px] text-[16px] no-underline"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" style={{ fill: 'none' }}>
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" style={{ fill: '#4285F4' }}/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" style={{ fill: '#34A853' }}/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" style={{ fill: '#FBBC05' }}/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" style={{ fill: '#EA4335' }}/>
              </svg>
              {t('emailConfirmation.openGmail')}
            </button>
            <button
              onClick={handleOpenOutlook}
              className="btn btn-secondary h-[42px] flex items-center justify-center gap-2 text-[16px] no-underline"
            >
              <img src="/assets/images/icons/outlook-icon.svg" alt="Outlook" className="w-5 h-5" />
              {t('emailConfirmation.openOutlook')}
            </button>
          </div>
          <div className="flex flex-col justify-center items-center gap-3 mb-6">
            {isResending && timer > 0 && (
              <span className="text-grey-50 dark:text-white text-sm">
                {timer} {t('emailConfirmation.seconds')}
              </span>
            )}
            <button
              onClick={handleResendEmail}
              disabled={isResending}
              className="btn btn-primary h-[42px] flex items-center justify-center text-[16px] no-underline w-full sm:w-auto"
            >
              {isResending ? t('emailConfirmation.resendEmail') : t('emailConfirmation.resendEmail')}
            </button>
          </div>
          {errorMessage && (
            <p className="text-red-600 dark:text-red-400 text-center mb-4 text-sm p-2 rounded bg-red-50 dark:bg-red-900/20">
              {errorMessage}
            </p>
          )}
    </PopupContainer>
  );
};

export default EmailConfirmationPage;
